import { TANK_COLORS, type LeaderboardRow } from '@tanky/shared';
import { api } from '../api';
import { h } from '../dom';
import { go, session } from '../session';
import { tankThumb } from '../game/tankArt';

export function pct(part: number, whole: number): string {
  return whole ? `${Math.round((part / whole) * 100)} %` : '–';
}

type Col = {
  key: string;
  label: string;
  title: string;
  value: (r: LeaderboardRow) => number;
  show: (r: LeaderboardRow) => string;
};

const COLS: Col[] = [
  { key: 'wins', label: 'Výhry', title: 'Počet výher', value: (r) => r.wins, show: (r) => String(r.wins) },
  { key: 'games', label: 'Hry', title: 'Dohrané hry', value: (r) => r.games, show: (r) => String(r.games) },
  { key: 'winrate', label: '% výher', title: 'Podíl výher', value: (r) => (r.games ? r.wins / r.games : 0), show: (r) => pct(r.wins, r.games) },
  { key: 'kills', label: 'Zabití', title: 'Zničené tanky soupeřů', value: (r) => r.kills, show: (r) => String(r.kills) },
  { key: 'damage', label: 'Škody', title: 'Způsobené škody soupeřům', value: (r) => r.damage, show: (r) => String(r.damage) },
  { key: 'acc', label: 'Přesnost', title: 'Výstřely, které zasáhly soupeře', value: (r) => (r.shots ? r.hits / r.shots : 0), show: (r) => pct(r.hits, r.shots) },
];

/** Karta s žebříčkem všech hráčů; řadí se klikem na hlavičku. */
export function leaderboardCard(): { el: HTMLElement; reload(): void } {
  let rows: LeaderboardRow[] = [];
  let sortKey = 'wins';
  const body = h('div', { class: 'table-wrap' }, h('p', { class: 'muted', style: { margin: '0' } }, 'Načítám…'));

  function render() {
    if (!rows.length) {
      body.replaceChildren(h('p', { class: 'muted', style: { margin: '0' } }, 'Zatím nikdo nedohrál žádnou hru.'));
      return;
    }
    const col = COLS.find((c) => c.key === sortKey)!;
    const sorted = rows.slice().sort((a, b) => col.value(b) - col.value(a) || b.wins - a.wins || b.damage - a.damage);
    body.replaceChildren(
      h(
        'table',
        { class: 'stats-table' },
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', {}, '#'),
            h('th', { class: 'name' }, 'Hráč'),
            ...COLS.map((c) =>
              h(
                'th',
                {
                  class: `sortable${c.key === sortKey ? ' sorted' : ''}`,
                  title: c.title,
                  onclick: () => {
                    sortKey = c.key;
                    render();
                  },
                },
                c.label,
              ),
            ),
          ),
        ),
        h(
          'tbody',
          {},
          ...sorted.map((r, i) =>
            h(
              'tr',
              { class: r.userId === session.me?.id ? 'me' : '', onclick: () => go(`/player/${r.userId}`) },
              h('td', { class: 'rank' }, i < 3 ? ['🥇', '🥈', '🥉'][i] : String(i + 1)),
              h('td', { class: 'name' }, h('span', { class: 'who' }, tankThumb(r.look, TANK_COLORS[i % TANK_COLORS.length], 30, r.userId), r.name)),
              ...COLS.map((c) => h('td', { class: c.key === sortKey ? 'sorted' : '' }, c.show(r))),
            ),
          ),
        ),
      ),
    );
  }

  async function reload() {
    try {
      rows = (await api.leaderboard()).rows;
      render();
    } catch (e) {
      body.replaceChildren(h('p', { class: 'error' }, (e as Error).message));
    }
  }

  reload();
  return { el: h('div', { class: 'card' }, h('h3', {}, '🏆 Žebříček'), body), reload };
}
